import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { allProjects } from '@/mocks/projects';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

const CATEGORIES_TR = ['Tümü', 'İlaç', 'Savunma Sanayi', 'Tekstil', 'Üretim', 'Ulaşım', 'Sağlık', 'Eğitim', 'Kültür Merkezi', 'Otel'];
const CATEGORIES_EN = ['All', 'Pharmaceutical', 'Defence Industry', 'Textile', 'Industrial Production', 'Transportation', 'Health', 'Education', 'Cultural Center', 'Hotel'];

export default function ProjectsPage() {
  const { i18n } = useTranslation();
  const isTr = i18n.language === 'tr';
  const [activeCategory, setActiveCategory] = useState(isTr ? 'Tümü' : 'All');

  useSEO({
    title: 'Referans Projeler | Canel Elektrik – Türkiye Endüstriyel Elektrik Projeleri',
    description:
      "150+ tamamlanan referans proje ile Canel Elektrik, ilaç, savunma sanayi, tekstil ve üretim tesislerinde Türkiye genelinde elektrik ve inşaat taahhüt hizmetleri sunmaktadır.",
    keywords:
      'Canel Elektrik projeler, endüstriyel elektrik projeleri Türkiye, ilaç fabrikası elektrik, savunma sanayi inşaat, referans projeler',
    canonical: '/projects',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Referans Projeler – Canel Elektrik',
      url: `${SITE_URL}/projects`,
      description:
        "Türkiye genelinde tamamlanan 150+ endüstriyel elektrik ve inşaat projesi portföyü.",
      provider: {
        '@type': 'Organization',
        name: 'Canel Elektrik İnşaat Taahhüt Mühendislik',
        url: SITE_URL,
      },
    },
  });

  const categories = isTr ? CATEGORIES_TR : CATEGORIES_EN;
  const allLabel = isTr ? 'Tümü' : 'All';

  useEffect(() => {
    setActiveCategory(isTr ? 'Tümü' : 'All');
  }, [isTr]);

  const filtered = activeCategory === allLabel
    ? allProjects
    : allProjects.filter((p) => (isTr ? p.category : p.categoryEn) === activeCategory);

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative h-72 md:h-80 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=industrial%20construction%20electrical%20engineering%20project%20site%20Turkey%20professional%20workers%20team%20large%20facility&width=1920&height=500&seq=proj-hero&orientation=landscape"
          alt={isTr ? 'Projeler' : 'Projects'}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A2332]/80 to-[#1A2332]/40" />
        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-16 max-w-7xl mx-auto w-full">
          <span className="text-[#E8A020] text-xs font-bold tracking-widest uppercase mb-3">
            {isTr ? 'PROJELER' : 'PROJECTS'}
          </span>
          <h1 className="text-3xl md:text-5xl font-black text-white">
            {isTr ? 'Referans Projelerimiz' : 'Reference Projects'}
          </h1>
          <p className="text-white/70 mt-3 text-base max-w-xl">
            {isTr
              ? '30+ yıllık deneyim ile tamamlanan referans projelerimiz.'
              : 'Our reference projects completed with 30+ years of experience.'}
          </p>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat ? 'bg-[#1A2332] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((project) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="group rounded-lg overflow-hidden border border-gray-100 hover:border-[#E8A020]/30 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full h-52 overflow-hidden">
                  <img
                    src={project.image}
                    alt={isTr ? project.title : project.titleEn}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-3 left-3">
                    <span className="bg-[#E8A020] text-white text-xs font-bold px-3 py-1 rounded-full">
                      {isTr ? project.category : project.categoryEn}
                    </span>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="bg-white/90 text-[#1A2332] text-xs font-bold px-4 py-2 rounded-full flex items-center gap-1.5">
                      <i className="ri-eye-line" />
                      {isTr ? 'Detayları Gör' : 'View Details'}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-[#1A2332] text-base mb-2 leading-snug group-hover:text-[#E8A020] transition-colors">
                    {isTr ? project.title : project.titleEn}
                  </h3>
                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-3 flex-wrap">
                    <span className="flex items-center gap-1"><i className="ri-map-pin-line" /> {project.location}</span>
                    {project.area && <span className="flex items-center gap-1"><i className="ri-layout-grid-line" /> {project.area}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(isTr ? project.tags : project.tagsEn).slice(0, 3).map((tag) => (
                      <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
